import { useTranslation } from 'react-i18next';
import { FiUser, FiPhone, FiMapPin } from 'react-icons/fi';

interface FamilyInfoProps {
  familyName: string | null;
  phone: string | null;
  country: string | null;
  state: string | null;
  isLoading?: boolean;
}

export default function FamilyInfo({ 
  familyName,
  phone,
  country,
  state,
  isLoading = false 
}: FamilyInfoProps) {
  const { t } = useTranslation();

  const formatPhoneNumber = (phone: string) => {
    // Remove any non-digit characters
    const cleaned = phone.replace(/\D/g, '');
    
    // Add + if it doesn't exist
    if (!phone.startsWith('+')) {
      return `+${cleaned}`;
    }
    return phone;
  };

  if (isLoading) {
    return (
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4"></div>
          <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
          <div className="space-y-3">
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-2/3"></div>
          </div>
        </div>
      </div>
    );
  }

  const location = [state, country].filter(Boolean).join(', ');

  return (
    <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
      <div className="px-6 py-5 border-b border-gray-200 dark:border-gray-700">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
          {t('familyInformation')}
        </h2>
      </div>
      
      <div className="px-6 py-5 space-y-4">
        {familyName && (
          <div className="flex items-center gap-x-3 text-gray-900 dark:text-white">
            <FiUser className="h-5 w-5 text-gray-400 dark:text-gray-500" />
            <span className="text-base">{familyName}</span>
          </div>
        )}
        
        {phone && (
          <div className="flex items-center gap-x-3 text-gray-900 dark:text-white">
            <FiPhone className="h-5 w-5 text-gray-400 dark:text-gray-500" />
            <span className="text-base">{formatPhoneNumber(phone)}</span>
          </div>
        )}

        {location && (
          <div className="flex items-center gap-x-3 text-gray-900 dark:text-white">
            <FiMapPin className="h-5 w-5 text-gray-400 dark:text-gray-500" />
            <span className="text-base">{location}</span>
          </div>
        )}
      </div>
    </div>
  );
}